import { useEffect, useMemo, useState } from 'react';
import { EndGameControl } from './EndGameControl';
import { ReactionBar } from './ReactionBar';
import { RecoveryNotice } from './RecoveryNotice';
import { saveCustomQuestion } from './lib/connections';
import {
  answerSameBrain,
  connectSessionCore,
  reactSameBrain,
  rememberActiveSession,
  type ConnectionState,
  type SessionSnapshot
} from './lib/session-core';
import { useDurablePairAnswer } from './lib/useDurablePairAnswer';
import { t, tf } from './lib/i18n';

export function CustomQuestionGame({ initialSessionId, initData, userId, initialSnapshot, onHome }: {
  initialSessionId: string;
  initData: string;
  userId: string;
  initialSnapshot: SessionSnapshot;
  onHome: (createChallenge?: boolean) => void;
}) {
  const [snapshot, setSnapshot] = useState<SessionSnapshot | null>(initialSnapshot);
  const [connection, setConnection] = useState<ConnectionState>('idle');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedLocally, setSavedLocally] = useState(Boolean(initialSnapshot.customPrompt?.saved));

  useEffect(() => connectSessionCore({
    sessionId: initialSessionId,
    initData,
    onState: setConnection,
    onSnapshot: (next) => {
      setSnapshot((current) => !current || next.session.revision >= current.session.revision ? next : current);
      if (next.customPrompt?.saved) setSavedLocally(true);
    }
  }), [initialSessionId, initData]);

  const durableAnswer = useDurablePairAnswer({
    sessionId: initialSessionId,
    initData,
    interaction: 'same_brain',
    snapshot,
    setSnapshot,
    submit: answerSameBrain
  });

  const customPrompt = snapshot?.customPrompt ?? null;
  const round = snapshot?.sameBrain.currentRound ?? null;
  const opponent = useMemo(() => snapshot?.players.find((player) => player.userId !== userId) ?? null, [snapshot, userId]);
  const endedBy = snapshot?.players.find((player) => player.explicitlyLeft);
  const myReaction = round?.reveal?.reactions.find((reaction) => reaction.userId === userId);
  const canSave = Boolean(customPrompt && customPrompt.creatorUserId === userId && !savedLocally && snapshot?.session.status === 'completed');

  const react = async (reaction: '😂' | '😳' | '🔥' | '🤝'): Promise<void> => {
    setBusy(true); setError(null);
    try { setSnapshot(await reactSameBrain(initialSessionId, initData, reaction)); }
    catch (reason) { setError(reason instanceof Error ? reason.message : t('Could not save that reaction.')); }
    finally { setBusy(false); }
  };

  const saveForUs = async (): Promise<void> => {
    if (!customPrompt || !opponent) return;
    setBusy(true); setError(null);
    try {
      await saveCustomQuestion(initData, opponent.userId, customPrompt.id);
      setSavedLocally(true);
    } catch (reason) { setError(reason instanceof Error ? reason.message : t('Could not save this question for you two.')); }
    finally { setBusy(false); }
  };

  // A loading screen must never trap the player: HOME forgets this game so the app opens normally next time.
  const leaveButton = <button className="text-action" type="button" onClick={() => { rememberActiveSession(null); onHome(false); }}>{t('HOME')}</button>;

  if (!snapshot || !customPrompt) return <main className="app-shell quiet-screen"><div className="brand-mark">UTAGE</div><p className="quiet-copy">{t('Restoring your question…')}</p>{leaveButton}</main>;

  if (endedBy) {
    const endedCopy = endedBy.userId === userId ? 'You ended this question.' : `${endedBy.firstName} ended this question.`;
    return <main className="app-shell quiet-screen energy-quiet"><div className="brand-mark">UTAGE</div><h1 className="error-title">{t('Question ended.')}</h1><p className="quiet-copy">{endedCopy}</p><button className="secondary-action" onClick={() => { rememberActiveSession(null); onHome(false); }}>{t('HOME')}</button></main>;
  }

  if (snapshot.session.status === 'expired') {
    return <main className="app-shell quiet-screen energy-quiet"><div className="brand-mark">UTAGE</div><h1 className="error-title">{t('This question expired.')}</h1><p className="quiet-copy">{t('Nothing social is implied. The session simply timed out.')}</p><button className="secondary-action" onClick={() => { rememberActiveSession(null); onHome(false); }}>{t('HOME')}</button></main>;
  }

  if (!round) return <main className="app-shell quiet-screen"><div className="brand-mark">UTAGE</div><p className="quiet-copy">{t('Opening the question…')}</p>{leaveButton}</main>;

  if (round.phase === 'revealed' && round.reveal) {
    const mine = round.reveal.answers.find((answer) => answer.userId === userId);
    const theirs = round.reveal.answers.find((answer) => answer.userId !== userId);
    return (
      <main className="app-shell game-screen custom-question-screen reveal-screen energy-expressive">
        <header className="game-header"><div className="brand-mark">UTAGE</div><span className="round-label">{t('YOUR QUESTION')}</span></header>
        <section className="reveal-stage custom-question-reveal" aria-live="polite">
          <p className="both-in">{t('BOTH IN.')}</p>
          <h1>{round.question}</h1>
          <div className="human-answer"><span>{t('YOU')}</span><strong>{mine?.label}</strong></div>
          <div className="human-answer"><span>{theirs?.firstName.toUpperCase() ?? t('THEM')}</span><strong>{theirs?.label}</strong></div>
          <h2 className={round.reveal.matched ? 'match-result' : 'different-result'}>{round.reveal.matched ? t('SAME CHOICE.') : t('DIFFERENT CHOICES.')}</h2>
          <p className="quiet-copy">{t('No score. You just learned one real thing about each other.')}</p>
          <ReactionBar mine={myReaction} received={round.reveal.reactions.filter((reaction) => reaction.userId !== userId)} disabled={busy} onReact={(reaction) => void react(reaction)} />
        </section>
        {canSave ? <button className="primary-action" type="button" disabled={busy} onClick={() => void saveForUs()}>{busy ? t('SAVING…') : t('SAVE THIS FOR US')}</button> : null}
        {savedLocally ? <p className="custom-saved-note">{t('SAVED FOR YOU TWO')}</p> : null}
        <button className={canSave ? 'secondary-wide' : 'primary-action'} type="button" onClick={() => { rememberActiveSession(null); onHome(false); }}>{t('HOME')}</button>
        {error ? <p className="inline-error" role="alert">{error}</p> : null}
      </main>
    );
  }

  return (
    <main className="app-shell game-screen custom-question-screen answer-screen energy-focused">
      <header className="game-header"><div className="brand-mark">UTAGE</div><span className="round-label">{t('YOUR QUESTION')}</span></header>
      <RecoveryNotice interrupted={connection !== 'live' && connection !== 'idle'} preserved="ANSWER PRESERVED" />
      <section className="question-stage">
        {opponent ? <p className="pair-human-anchor">{opponent.firstName} · {opponent.presence === 'reconnecting' ? t('reconnecting…') : round.opponentLocked ? t('locked in') : t('here')}</p> : null}
        <p className="custom-authored-label">{customPrompt.creatorUserId === userId ? t('YOU ASKED') : t('THEY ASKED')}</p>
        <h1>{round.question}</h1>
        <div className="answer-options">
          {round.options.map((option) => {
            const selected = round.myAnswer?.key === option.key || durableAnswer.pendingChoice === option.key;
            return <button key={`${round.id}-${option.key}`} className={selected ? 'answer-option selected' : 'answer-option'} aria-pressed={selected} disabled={busy || durableAnswer.answerBusy || Boolean(round.myAnswer) || Boolean(durableAnswer.pendingChoice)} onClick={() => durableAnswer.choose(option.key, option.label, round.id, 1, snapshot.session.revision)}><span>{option.label}</span>{round.myAnswer?.key === option.key ? <small>{t('✓ LOCKED')}</small> : durableAnswer.pendingChoice === option.key ? <small>{t('CONFIRMING…')}</small> : null}</button>;
          })}
        </div>
        {round.myAnswer ? <p className="waiting-copy">{opponent?.presence === 'reconnecting' ? tf('{name} is reconnecting…', { name: opponent.firstName }) : round.opponentLocked ? `${opponent?.firstName ?? t('They')} ✓` : tf('{name} is choosing…', { name: opponent?.firstName ?? t('They') })}</p> : null}
        {durableAnswer.answerError ? <p className="inline-error" role="alert">{durableAnswer.answerError} <button className="inline-retry" type="button" disabled={durableAnswer.answerBusy} onClick={durableAnswer.retry}>{durableAnswer.answerBusy ? t('TRYING…') : t('TRY AGAIN')}</button></p> : null}
        {error ? <p className="inline-error" role="alert">{error}</p> : null}
      </section>
      <EndGameControl sessionId={initialSessionId} initData={initData} userId={userId} onEnded={() => onHome(false)} />
    </main>
  );
}
